'use client';

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { createClient } from '@/lib/supabase/client';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  /** 'free' | 'pro' — server truth from /api/me; 'free' when anonymous. */
  plan: string;
  /** Convenience: plan !== 'free'. Watermark-free exports are a paid perk. */
  isPaid: boolean;
  /** Re-fetch the plan (e.g. after returning from Stripe checkout). */
  refreshPlan: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  isLoading: true,
  plan: 'free',
  isPaid: false,
  refreshPlan: async () => {},
  signOut: async () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

async function logUserActivity(userId: string) {
  try {
    const supabase = createClient();
    await supabase
      .from('user_logins')
      .insert({
        user_id: userId,
        login_date: new Date().toISOString().split('T')[0],
        logged_at: new Date().toISOString(),
      });
  } catch {
    // Non-critical — don't break the app if logging fails
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [plan, setPlan] = useState('free');

  const refreshPlan = useCallback(async () => {
    try {
      const r = await fetch('/api/me', { cache: 'no-store' });
      const d = await r.json().catch(() => null);
      setPlan(d?.user?.plan ?? 'free');
    } catch {
      // keep the last known plan
    }
  }, []);

  // Once per signed-in session: /api/me does the profile upsert server-side
  // AND hands back the plan that gates the watermark.
  useEffect(() => {
    if (!user?.id) {
      setPlan('free');
      return;
    }
    refreshPlan();
  }, [user?.id, refreshPlan]);

  useEffect(() => {
    let subscription: { unsubscribe: () => void } | null = null;
    let cancelled = false;
    let activityLogged = false;

    const init = () => {
      if (cancelled) return;
      const supabase = createClient();

      supabase.auth.getSession().then(({ data: { session } }) => {
        if (cancelled) return;
        setSession(session);
        setUser(session?.user ?? null);
        setIsLoading(false);
        if (session?.user && !activityLogged) {
          activityLogged = true;
          logUserActivity(session.user.id);
        }
      });

      const { data: { subscription: sub } } = supabase.auth.onAuthStateChange((event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setIsLoading(false);
        if (event === 'SIGNED_IN' && session?.user && !activityLogged) {
          activityLogged = true;
          logUserActivity(session.user.id);
        }
      });
      subscription = sub;
    };

    let idleId: number | undefined;
    if (typeof requestIdleCallback !== 'undefined') {
      idleId = requestIdleCallback(init);
    } else {
      setTimeout(init, 1);
    }

    return () => {
      cancelled = true;
      if (idleId !== undefined && typeof cancelIdleCallback !== 'undefined') {
        cancelIdleCallback(idleId);
      }
      subscription?.unsubscribe();
    };
  }, []);

  const signOut = useCallback(async () => {
    const supabase = createClient();
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, session, isLoading, plan, isPaid: plan !== 'free', refreshPlan, signOut }}
    >
      {children}
    </AuthContext.Provider>
  );
}
